import clsx from 'clsx';
import React, { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Spin, Upload } from 'antd';

import { Button, ButtonType } from "../../../components/styled/Button";
import { SimpleList, ListType } from "../../../components/styled/SimpleList";
import { IconArrowRight, IconExport } from '../../../components/styled/icons';
import { ProjectSettingsKeys } from '../../../enums/locales/modals';

import { selectProfileSettingsVisible } from '../../../redux/modals/selectors';
import { ResourceCard } from './ResourceCard';

import { Wrapper } from './ProfileSettings.styled';
import { modalsActions } from '../../../redux/modals/actions';
import { profileSettingsActions } from '../../../redux/profile-settings/actions';
import { ProfileForm, ProfileFormBag } from '../../forms/ProfileForm';
import { selectProfileName, selectProfilePicture, selectUI } from '../../../redux/auth/selectors';
import { selectUI as selectProfileUI, selectUsers } from '../../../redux/profile-settings/selectors';
import { ProfileAvatar } from '../../../components/ProfileAvatar';
import { CompanyForm } from '../../forms/CompanyForm';
import { validateEmail } from '../../../utils/common';
import { FieldListTitleKeys, FieldPlaceholderKeys } from '../../../enums/locales/forms';

const ProfileSettings: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const visible = useSelector(selectProfileSettingsVisible);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const avatarUrl = useSelector(selectProfilePicture);
  const name = useSelector(selectProfileName);
  const usersList = useSelector(selectUsers);
  const { loading: userLoading } = useSelector(selectUI);
  const { passwordReseted, passwordReseting, loading } = useSelector(selectProfileUI);

  const onReset = useCallback(() => {
    dispatch(profileSettingsActions.resetPasswordStart());
  }, [dispatch]);

  const onBackClick = useCallback((e) => {
    const pathOfSvgRef = svgRef.current?.children[0].children[1];
    if (e.target === ref.current || e.target === btnRef.current || e.target === svgRef.current || e.target === pathOfSvgRef) {
      dispatch(modalsActions.profileSettingsHide());
      ProfileFormBag.resetForm();
      dispatch(profileSettingsActions.reset());
    }
  }, [dispatch]);

  const onSubmit = useCallback((values) => {
    dispatch(profileSettingsActions.personalRefresh(values));
    dispatch(profileSettingsActions.personalSave());
  }, [dispatch]);

  const onCompanySubmit = useCallback((values) => {
    dispatch(profileSettingsActions.companyRefresh(values));
    dispatch(profileSettingsActions.companySave());
  }, [dispatch]);

  const onUsersChange = useCallback((values) => {
    dispatch(profileSettingsActions.usersUpdate(values));
  }, [dispatch]);

  const onUpload = useCallback(({ file }) => {
    dispatch(profileSettingsActions.avatarUpload(file));
  }, [dispatch]);

  const customValidation = useCallback((email) => {
    const isAdded = usersList.includes(email);
    return validateEmail(email) && !isAdded;
  }, [usersList]);

  const isUploadable = useMemo(() => !(loading || userLoading), [userLoading, loading]);
  const resetedMessage = useMemo(() => {
    if (passwordReseted === null) {
      return '';
    }
    if (passwordReseted) {
      return ProjectSettingsKeys.ResetedSuccessful;
    }

    return ProjectSettingsKeys.ResetedError;
  }, [passwordReseted]);

  const resetBtnText = useMemo(() => passwordReseted ? t(ProjectSettingsKeys.ResetResend) : t(ProjectSettingsKeys.ResetPassword), [t, passwordReseted]);

  return (
    <Wrapper
      ref={ref}
      onMouseDown={onBackClick}
      className={clsx({ disapear: !visible })}
      visible={visible}
    >
      <div className="sider-mask"></div>
      <div className="sider-body">
        <div className="sider-header">
          <div ref={btnRef} onClick={onBackClick} className="btn-back">
            <IconArrowRight ref={svgRef} />
          </div>
          {t(ProjectSettingsKeys.ProfileSettings)}
        </div>
        <div className="sider-body-content">
          {visible && (
            <>
              <div className="sider-body-content-form">
                <div className="avatar-container">
                  <div className="avatar-image">
                    {!isUploadable && <Spin size="large" />}
                    {isUploadable && (
                      <Upload fileList={[]} customRequest={onUpload}>
                        <div className="mask">
                          <IconExport />
                        </div>
                        <ProfileAvatar size={100} name={name} url={avatarUrl} />
                      </Upload>
                    )}
                  </div>
                  <div className="description">
                    {t(ProjectSettingsKeys.AvatarDescription)}
                  </div>
                </div>
                <ProfileForm onSubmit={onSubmit} />
                <div className="security-container">
                  <div className="security-title">
                    {t(ProjectSettingsKeys.Security)}
                  </div>
                  <div className="password-reset">
                    <div className="password-reset-label">{t(ProjectSettingsKeys.PasswordLabel)}</div>
                    <div className="password-reset-button">
                      <Button
                        type={ButtonType.Secondary}
                        onClick={onReset}
                        disabled={passwordReseting}
                        title={resetBtnText}
                      />
                      <div className={clsx("reset-info", { success: !!passwordReseted })}>
                        {t(resetedMessage)}
                      </div>
                    </div>
                  </div>
                </div>
                <CompanyForm onSubmit={onCompanySubmit} />
                <div className="manage-user">
                  <div className="manage-user-title">{t(ProjectSettingsKeys.ManageUsers)}</div>
                  <div className="manage-user-subtitle">{t(ProjectSettingsKeys.ManageUsersSubtitle)}</div>
                  <SimpleList
                    listType={ListType.Invites}
                    customValidate={customValidation}
                    listTitle={FieldListTitleKeys.Invites}
                    value={usersList}
                    addPlaceholder={FieldPlaceholderKeys.AddUsers}
                    onChange={onUsersChange}
                  />
                </div>
              </div>
              <div className="sider-body-content-meta">
                <div className="navigation">
                  <ResourceCard />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Wrapper>
  );
};

export default ProfileSettings;